import React, { useState, useEffect } from 'react';
import { 
  FileText, Plus, Edit, Eye, Check, X, History, GitBranch, Save
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { useToast } from '../../../hooks/use-toast';

const PromptsView = () => {
  const [prompts, setPrompts] = useState([]);
  const [brandVoice, setBrandVoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFeature, setSelectedFeature] = useState('all');
  const [showBrandVoice, setShowBrandVoice] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const API_BASE_URL = '/api';

  const features = [
    { id: 'all', label: 'All Features' },
    { id: 'affirmations', label: 'Affirmations' },
    { id: 'journaling', label: 'Journaling' },
    { id: 'companion', label: 'Conchi' },
    { id: 'divine_guidance', label: 'Divine Guidance' }
  ];

  useEffect(() => {
    fetchPrompts();
    fetchBrandVoice();
  }, [selectedFeature]);

  const fetchPrompts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const featureParam = selectedFeature !== 'all' ? `?feature_name=${selectedFeature}` : '';
      const response = await fetch(`${API_BASE_URL}/ai-controls/prompts${featureParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch prompts');

      const data = await response.json();
      setPrompts(data.data || []);
    } catch (error) {
      toast({
        title: '❌ Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBrandVoice = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/ai-controls/brand-voice`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setBrandVoice(data.data);
      }
    } catch (error) {
      console.error('Error fetching brand voice:', error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      published: 'bg-green-100 text-green-800 border-green-200',
      draft: 'bg-gray-100 text-gray-800 border-gray-200',
      preview: 'bg-blue-100 text-blue-800 border-blue-200',
      archived: 'bg-amber-100 text-amber-800 border-amber-200'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatFeatureName = (name) => {
    return name.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const handleEditPrompt = (prompt) => {
    setEditingPrompt({...prompt});
    setShowEditor(true);
  };

  const handleSavePrompt = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      // Create new version by copying
      const newVersion = {
        ...editingPrompt,
        version: (parseFloat(editingPrompt.version) + 0.1).toFixed(1),
        status: 'draft',
        parent_version: editingPrompt.version
      };
      
      delete newVersion.id;
      delete newVersion._id;
      delete newVersion.created_at;
      delete newVersion.updated_at;
      
      const response = await fetch(`${API_BASE_URL}/ai-controls/prompts`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newVersion)
      });

      if (!response.ok) throw new Error('Failed to save prompt');

      toast({
        title: '✅ Prompt Saved',
        description: `New version ${newVersion.version} created as draft`,
        variant: 'default'
      });

      setShowEditor(false);
      setEditingPrompt(null);
      fetchPrompts();
    } catch (error) {
      toast({
        title: '❌ Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePublishPrompt = async (promptId, notes) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/ai-controls/prompts/${promptId}/publish`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: notes || 'Published from admin panel' })
      });

      if (!response.ok) throw new Error('Failed to publish prompt');

      toast({
        title: '✅ Published',
        description: 'Prompt is now live',
        variant: 'default'
      });

      fetchPrompts();
    } catch (error) {
      toast({
        title: '❌ Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
            Prompt Templates & Brand Voice
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Edit prompts with versioning, rollback, and brand voice consistency
          </p>
        </div>
        <Button
          onClick={() => setShowBrandVoice(!showBrandVoice)}
          className="bg-gradient-to-r from-pink-500 to-rose-500"
        >
          <FileText className="w-4 h-4 mr-2" />
          {showBrandVoice ? 'Hide' : 'View'} Brand Voice
        </Button>
      </div>

      {/* Brand Voice Card */}
      {showBrandVoice && brandVoice && (
        <Card className="p-6 bg-gradient-to-r from-pink-50 to-rose-50 border-2 border-pink-200">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-6 h-6 text-pink-600" />
            <h3 className="text-lg font-semibold text-gray-900">NeuroLove™ Brand Voice</h3>
            <span className="text-xs text-gray-600">Version {brandVoice.version}</span>
          </div>

          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Guidelines</h4>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{brandVoice.guidelines}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-green-700 mb-2">✅ DO</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  {brandVoice.do_list && brandVoice.do_list.map((item, i) => (
                    <li key={i}>• {item}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-red-700 mb-2">❌ DON'T</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  {brandVoice.dont_list && brandVoice.dont_list.map((item, i) => (
                    <li key={i}>• {item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Feature Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {features.map(feature => (
          <button
            key={feature.id}
            onClick={() => setSelectedFeature(feature.id)}
            className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
              selectedFeature === feature.id
                ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                : 'bg-white text-gray-600 hover:text-gray-900 border border-gray-200'
            }`}
          >
            {feature.label}
          </button>
        ))}
      </div>

      {/* Prompts List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : prompts.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No prompt templates found</p>
          <p className="text-sm text-gray-500 mt-2">Import your configs to get started</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {prompts.map(prompt => (
            <Card key={prompt.id} className="p-6 hover:shadow-lg transition-all border-2 border-gray-100">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-900">
                      {formatFeatureName(prompt.feature_name)} - {prompt.template_name}
                    </h3>
                    {getStatusBadge(prompt.status)}
                  </div>
                  <div className="text-xs text-gray-500">
                    Version {prompt.version} · Last updated {new Date(prompt.updated_at).toLocaleDateString()}
                  </div>
                </div>
              </div>

              {/* System Message Preview */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="text-xs text-gray-600 mb-2">System Message Preview:</div>
                <div className="text-sm text-gray-800 line-clamp-3">
                  {prompt.system_message}
                </div>
              </div>

              {/* Variables */}
              {prompt.variables && prompt.variables.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs text-gray-600 mb-2">Variables:</div>
                  <div className="flex flex-wrap gap-2">
                    {prompt.variables.map((variable, i) => (
                      <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-mono">
                        {`{{${variable}}}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="flex-1 text-xs"
                  onClick={() => handleEditPrompt(prompt)}
                >
                  <Edit className="w-3 h-3 mr-1" />
                  Edit
                </Button>
                {prompt.status === 'published' ? (
                  <Button 
                    variant="outline"
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={() => handleEditPrompt(prompt)}
                  >
                    <GitBranch className="w-3 h-3 mr-1" />
                    New Version
                  </Button>
                ) : (
                  <Button 
                    size="sm"
                    className="flex-1 text-xs bg-gradient-to-r from-blue-500 to-cyan-500"
                    onClick={() => handlePublishPrompt(prompt.id)}
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Publish
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Prompt Editor Modal */}
      {showEditor && editingPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Edit Prompt Template
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {formatFeatureName(editingPrompt.feature_name)} - {editingPrompt.template_name}
                </p>
              </div>
              <button 
                onClick={() => setShowEditor(false)} 
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* System Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  System Message
                </label>
                <textarea
                  value={editingPrompt.system_message}
                  onChange={(e) => setEditingPrompt({...editingPrompt, system_message: e.target.value})}
                  rows={8}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  placeholder="Enter the AI system prompt..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  This is the core instruction that defines how the AI behaves for this feature
                </p>
              </div>

              {/* Tone Guidelines */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tone Guidelines
                </label>
                <input
                  type="text"
                  value={editingPrompt.tone_guidelines || ''}
                  onChange={(e) => setEditingPrompt({...editingPrompt, tone_guidelines: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Warm, compassionate, empowering"
                />
              </div>

              {/* Variables */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Variables (comma-separated)
                </label>
                <input
                  type="text"
                  value={editingPrompt.variables ? editingPrompt.variables.join(', ') : ''}
                  onChange={(e) => setEditingPrompt({
                    ...editingPrompt, 
                    variables: e.target.value.split(',').map(v => v.trim()).filter(Boolean)
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., mood, user_name, context"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use in prompt as: {`{{variable_name}}`}
                </p>
              </div>

              {/* Do List */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Do List (one per line)
                </label>
                <textarea
                  value={editingPrompt.do_list ? editingPrompt.do_list.join('\n') : ''}
                  onChange={(e) => setEditingPrompt({
                    ...editingPrompt,
                    do_list: e.target.value.split('\n').filter(Boolean)
                  })}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="Things the AI should do..."
                />
              </div>

              {/* Don't List */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Don't List (one per line)
                </label>
                <textarea
                  value={editingPrompt.dont_list ? editingPrompt.dont_list.join('\n') : ''}
                  onChange={(e) => setEditingPrompt({
                    ...editingPrompt,
                    dont_list: e.target.value.split('\n').filter(Boolean)
                  })}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="Things the AI should avoid..."
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => setShowEditor(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSavePrompt}
                  disabled={saving}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save as New Version
                    </>
                  )}
                </Button>
              </div>

              <p className="text-xs text-gray-500 text-center">
                💡 Saving creates a new draft version. Publish it to make it live.
              </p>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PromptsView;